import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Resource } from '@mode/shared/util-halogen';
import { annotateSqlWithSyntaxError } from '@mode/shared/util-js';
import * as angular from 'angular';
import _ from 'lodash';
import { ModeLinkFactoryService } from '../../api/mode-link.factory';
import { getHrefFromResource } from '../get-href-from-resource';
import { ModeModelBase, ModeModelInjectables } from '../model';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';

function buildCreator(reportRun) {
  if (reportRun.account) {
    return reportRun;
  }
  return reportRun
    .executedBy({ useEntryPath: true })
    .then((account) => {
      reportRun.account = account;
      return reportRun;
    })
    .catch((e) => {
      return reportRun;
    });
}

export class ReportRunModel extends ModeModelBase {
  override _name = 'reportRun';

  reportScheduleToken: any;
  parameters: any;
  limit_selected: any;
  link: string;
  embed: string;
  shareLink: string;
  share: string;
  embedLink: string;
  iframeLink: string;
  externalLink: any;
  secretEmbedLink: string;
  secretIframeLink: string;
  completedAt: string | number | Date;
  startedAt: any;
  createdAt: any;
  state: any;
  errorLineHtml: any;
  errorMessage: string;
  rendered_source: string;

  constructor(
    injectables: ModeModelInjectables,
    private modeLinkFactory: ModeLinkFactoryService,
    private domSanitizer: DomSanitizer
  ) {
    super(injectables);
  }

  override idAttribute = 'token';
  override belongsTo = 'report';
  override parentIdAttribute = 'token';
  override hasOne = [{ name: 'account', as: 'executedBy', idAttribute: 'username' }, { name: 'pdfExport' }];
  override hasMany = [
    { name: 'queryRuns', idAttribute: 'token' },
    { name: 'reportRunEmails' },
    { name: 'reportRunSlackMessages' },
    { name: 'pythonCellRuns', idAttribute: 'token' },
  ];
  override attributes = [
    { name: 'parameters', default: { limit: 100 } },
    { name: 'token', default: null },
    { name: 'state', default: 'new' },
    { name: 'errorMessage', default: '' },
    { name: 'errorDetail', default: '' },
    { name: 'dataSourceId', default: null },
    { name: 'createdAt', default: '' },
    { name: 'localCreatedAt', default: '', abstract: true },
    { name: 'startedAt', default: '' },
    { name: 'completedAt', default: '' },
    { name: 'share', default: '' },
    { name: 'embed', default: '' },
    { name: 'link', default: '' },
    { name: 'shareLink', default: '' },
    { name: 'embedLink', default: '' },
    { name: 'iframeLink', default: '' },
    { name: 'limit_selected', default: true },
    { name: 'python_state', default: 'none' },
    { name: 'form_fields', default: [] },
  ];
  override initialize(resource) {
    if (angular.isDefined(resource) && angular.isDefined(resource.parameters)) {
      if (resource._links && resource._links.report_schedule) {
        this.reportScheduleToken = resource._links.report_schedule.href;
      }
      if (angular.isDefined(resource.parameters.limit) && angular.isDefined(resource.parameters.selected)) {
        // this is an intialization of an existing report run
        this.parameters.limit = resource.parameters.limit;
        this.limit_selected = resource.parameters.selected;
      } else if (angular.isDefined(resource.parameters.limit)) {
        // limit is set from api so make limit selected
        this.parameters.limit = resource.parameters.limit;
        this.limit_selected = true;
      } else {
        this.limit_selected = false;
      }

      const domain = location.protocol + '//' + document.domain + (location.port ? ':' + location.port : '');
      this.link = domain + this.embed;
      this.shareLink = domain + this.share;
      this.embedLink =
        '<a href="' +
        this.link +
        '" class="mode-embed">Mode Analytics</a><script src="' +
        domain +
        '/embed/embed.js"></script>';
      this.iframeLink = '<iframe src="' + this.link + '" width="100%" height="300" frameborder="0"></iframe>';
      this.externalLink = getHrefFromResource(resource, 'web_external_url');

      this.generateErrorLine();
    }
  }

  initEmbedKey(embedKey) {
    const domain = location.protocol + '//' + document.domain + (location.port ? ':' + location.port : '');
    this.secretEmbedLink =
      '<a href="' +
      this.link +
      '?secret_key=' +
      embedKey.value +
      '" class="mode-embed">Mode Analysis</a><script src="' +
      domain +
      '/embed/embed.js"></script>';
    this.secretIframeLink =
      '<iframe src="' +
      this.link +
      '?secret_key=' +
      embedKey.value +
      '" width="100%" height="300" frameborder="0"></iframe>';

    this._resource._links['embed_key'] = this.modeLinkFactory.create(Resource, {
      href: embedKey._resource.links('self').href,
    });
  }

  cleanupEmbedKey() {
    this.secretEmbedLink = null;
    this.secretIframeLink = null;

    this._resource._links['embed_key'] = null;
  }

  timeCompleted() {
    const end = new Date(this.completedAt);
    const start = new Date(this.startedAt || this.createdAt);
    const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    const minutes = Math.floor((end.getTime() - start.getTime()) / 60 / 1000);
    if (minutes > 0) {
      return minutes > 1 ? minutes + ' Minutes' : minutes + ' Minute';
    } else if (seconds > 0) {
      return seconds > 1 ? seconds + ' Seconds' : seconds + ' Second';
    } else {
      return end.getTime() - start.getTime() + 'ms';
    }
  }

  stateText() {
    switch (this.state) {
      case 'failed':
        return 'failed';
      case 'cancelled':
        return 'cancelled';
      case 'succeeded':
        return '';
      default:
        return 'running';
    }
  }

  generateErrorLine() {
    if (this.errorLineHtml) {
      return;
    }

    const result = annotateSqlWithSyntaxError({
      state: this.state,
      errorMessage: this.errorMessage,
      renderedSource: this.rendered_source,
    });

    if (result) {
      this.errorLineHtml = this.domSanitizer.bypassSecurityTrustHtml(result);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class ReportRunModelAccessor extends ModelAccessorBase<ReportRunModel> {
  override name = 'reportRun';
  override idAttribute = 'token';
  override parentModel = 'report';
  override parentIdAttribute = 'token';
  override createAttributes = ['account'];

  constructor(
    injectables: ModelAccessorInjectables,
    private modeLinkFactory: ModeLinkFactoryService,
    private domSanitizer: DomSanitizer
  ) {
    super(injectables, 'migrated', 'mode.reportRun');

    this.getIdFromLink = (link) => {
      return link.split('/runs/')[1].split('?')[0];
    };
    this.customCreateFunction = (args) => {
      const deferred = this.injectables.$q.defer();

      const accountModel = this.injectables.modelNameService.get('account');
      accountModel
        .find({ username: args['username'], useEntryPath: true })
        .then((account) => {
          const report = account.new_reports(),
            reportRun = report.embedded('new_report_run');

          if (
            angular.isUndefined(reportRun) ||
            !angular.isFunction(reportRun.hasForm) ||
            !reportRun.hasForm('create')
          ) {
            deferred.reject(new Error('Create path not found'));
          }

          reportRun.forms('create').$submit(args.params).then(deferred.resolve).catch(deferred.reject);
        })
        .catch(deferred.reject);

      return deferred.promise;
    };
  }

  override createModel(resource, opts?) {
    const model = new ReportRunModel(this.injectables.modelInjectables, this.modeLinkFactory, this.domSanitizer);
    model.$initialize(resource, opts);
    return model;
  }

  // helper functions
  buildRunsWithCreator(reportRuns) {
    return this.injectables.$q.all(_.map(reportRuns, buildCreator));
  }
}
